/**
 * The board.
 *
 * This is a digital BOARD game, so the dungeon has to be a board you look at:
 * tiles laid out in space, corridors between them, your piece standing on one of
 * them, and fog over what you have not seen. A list of destination buttons
 * answers "where can I go" but never shows you *where you are*, which is the
 * whole reason a board exists.
 *
 * Everything is one SVG scaled to the container, so it works from 360px up
 * without pan/zoom controls getting in the way on a phone.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { boardEdges, restedFlag, visibleRooms, type GameSession, type RoomDef } from '@lastroll/engine';
import {
  KIND_STYLE,
  renderBoardSheet,
  renderMapTile,
  renderPlayerToken,
  type RoomState,
} from '@lastroll/art';

/**
 * Grid geometry in SVG units. What decides how big a tile LOOKS is its share of
 * the viewBox width, not its absolute size — the svg scales to its container. So
 * the tile grew and the cell spacing and padding shrank around it.
 */
const CELL_W = 168;
/**
 * Row pitch must clear the tile PLUS its caption, or a space's name prints on
 * top of the space below it. CELL_H = TILE + caption band.
 */
const CAPTION_BAND = 46;
const TILE = 150;
const CELL_H = TILE + CAPTION_BAND;
const PAD = 18;

const dataUri = (svg: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

/** Fog radius in hops. Two keeps the next decision visible without spoiling. */
const FOG_RADIUS = 2;

export interface DungeonMapProps {
  session: GameSession;
  onMove: (roomId: string) => void;
  /** Destinations the current roll can pay for, with their cost. */
  reachable: { room: string; cost: number }[];
}

export function DungeonMap({ session, onMove, reachable }: DungeonMapProps): JSX.Element {
  const dungeon = session.content.dungeon;
  const { world, currentRoom } = session.state;
  const [expanded, setExpanded] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Escape closes the expanded map, the same as tapping the button.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const edges = useMemo(() => boardEdges(dungeon), [dungeon]);

  const visible = useMemo(
    () =>
      currentRoom
        ? visibleRooms(dungeon, currentRoom, FOG_RADIUS, {
            flags: world.flags,
            discoveredSecrets: world.discoveredSecrets,
          })
        : new Set<string>(),
    /*
     * 크기로 건다. `flags` 와 `discoveredSecrets` 는 리듀서가 **제자리에서**
     * 채우는 Set 이라 참조가 영원히 그대로다 — 의존성 배열에 객체를 그대로
     * 넣으면 React 는 "안 바뀌었다"고 보고 다시 계산하지 않는다. 그래서 방을
     * 옮기지 않고 비밀을 발견하면 안개가 걷히지 않았다. 두 집합은 늘어나기만
     * 하므로 크기가 정확한 신호다.
     */
    [dungeon, currentRoom, world.flags.size, world.discoveredSecrets.size],
  );

  const reachableById = useMemo(
    () => new Map(reachable.map((r) => [r.room, r.cost])),
    [reachable],
  );

  const rooms = dungeon.rooms;
  const cols = Math.max(...rooms.map((r) => r.pos.x)) + 1;
  const rows = Math.max(...rooms.map((r) => r.pos.y)) + 1;
  const width = (cols - 1) * CELL_W + TILE + PAD * 2;
  // Extra tail so the last row's caption is not clipped by the sheet edge.
  const height = (rows - 1) * CELL_H + TILE + PAD * 2 + 46;

  const center = (room: RoomDef): { x: number; y: number } => ({
    x: PAD + room.pos.x * CELL_W + TILE / 2,
    y: PAD + room.pos.y * CELL_H + TILE * 0.5,
  });

  /** Rooms visited at least once — anything cleared, plus where you stand. */
  const seen = (id: string): boolean =>
    world.clearedRooms.has(id) || id === currentRoom || visible.has(id);

  const stateOf = (room: RoomDef): RoomState => {
    if (room.id === currentRoom) return 'current';
    if (reachableById.has(room.id)) return 'reachable';
    if (world.clearedRooms.has(room.id)) return 'cleared';
    if (visible.has(room.id)) return 'known';
    return 'fogged';
  };

  const byId = new Map(rooms.map((r) => [r.id, r]));

  /*
   * Opening the zoom on a board wider than the screen dumps you at the far-left
   * corner, which is usually empty paper — your piece is somewhere else. Scroll
   * so the piece is what you see first.
   */
  const here = currentRoom ? byId.get(currentRoom) : undefined;
  const hereX = here ? center(here).x / width : 0.5;
  useEffect(() => {
    const box = boxRef.current;
    if (!expanded || !box) return;
    box.scrollLeft = Math.max(0, hereX * box.scrollWidth - box.clientWidth / 2);
  }, [expanded, hereX]);

  return (
    <div
      className={`map${expanded ? ' map--expanded' : ''}`}
      ref={boxRef}
      /* The sheet's own proportions, so CSS can cap the box without guessing. */
      style={{ ['--map-ar' as string]: String(width / height) }}
    >
      <button
        type="button"
        className="map__expand"
        onClick={() => setExpanded((v) => !v)}
        aria-label={expanded ? '지도 작게 보기' : '지도 크게 보기'}
      >
        {expanded ? '✕' : '⤢'}
      </button>
      <svg
        className="map__svg"
        viewBox={`0 0 ${width} ${height}`}
        role="group"
        aria-label="던전 지도"
      >
        {/* The cardstock the board is printed on: grain, yellowed edges, fold. */}
        <image
          href={dataUri(renderBoardSheet(width, height, 'aurelia', dungeon.def.id))}
          x={0}
          y={0}
          width={width}
          height={height}
        />

        {/* Corridors first, so tiles sit on top of them. */}
        <g className="map__corridors">
          {edges.map((e) => {
            const a = byId.get(e.from);
            const b = byId.get(e.to);
            if (!a || !b) return null;
            // A corridor is drawn once either end is known; a hidden passage only
            // once its secret is found, so the map never leaks a secret room.
            const known = seen(a.id) || seen(b.id);
            if (!known) return null;
            if (e.hidden && !world.discoveredSecrets.has(byId.get(e.to)?.secretId ?? '')) {
              return null;
            }
            const pa = center(a);
            const pb = center(b);
            const live =
              (a.id === currentRoom && reachableById.has(b.id)) ||
              (b.id === currentRoom && reachableById.has(a.id));
            return (
              <g key={`${e.from}|${e.to}`}>
                <line
                  x1={pa.x}
                  y1={pa.y}
                  x2={pb.x}
                  y2={pb.y}
                  className={`corridor${live ? ' corridor--live' : ''}${e.hidden ? ' corridor--hidden' : ''}`}
                />
                {live && (
                  <circle className="corridor__pip" cx={(pa.x + pb.x) / 2} cy={(pa.y + pb.y) / 2} r={5} />
                )}
              </g>
            );
          })}
        </g>

        {/* Tiles, painted back-to-front so lower rows overlap correctly. */}
        {[...rooms]
          .sort((a, b) => a.pos.y - b.pos.y || a.pos.x - b.pos.x)
          .map((room) => {
            const st = stateOf(room);
            const p = center(room);
            const cost = reachableById.get(room.id);
            const clickable = st === 'reachable';
            const label =
              st === 'fogged'
                ? '아직 모르는 방'
                : `${room.name} · ${KIND_STYLE[room.kind].label}${cost !== undefined ? ` · ${cost}칸` : ''}`;

            return (
              <g
                key={room.id}
                className={`tile tile--${st}${clickable ? ' tile--clickable' : ''}`}
                transform={`translate(${p.x - TILE / 2} ${p.y - TILE / 2})`}
                onClick={
                  clickable
                    ? () => {
                        setExpanded(false);
                        onMove(room.id);
                      }
                    : undefined
                }
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                aria-label={label}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpanded(false);
                          onMove(room.id);
                        }
                      }
                    : undefined
                }
              >
                <image
                  href={dataUri(
                    renderMapTile({
                      kind: room.kind,
                      state: st,
                      size: TILE,
                      seed: room.id,
                      ...(cost !== undefined ? { cost } : {}),
                    }),
                  )}
                  width={TILE}
                  height={TILE}
                />
                {st !== 'fogged' && (
                  <text className="tile__name" x={TILE / 2} y={TILE + 20} textAnchor="middle">
                    {room.name}
                    {/* 쉴 곳 (GDD §12-1) — 안 쓴 곳은 「쉼」, 쓴 곳은 「잤음」. 지도가 먼저 알려 준다. */}
                    {room.rest && (
                      <tspan className="tile__rest">
                        {session.state.world.flags.has(restedFlag(room.id)) ? ' · 잤음' : ' · 쉼'}
                      </tspan>
                    )}
                  </text>
                )}
              </g>
            );
          })}

        {/* The player's piece, last so it stands above every tile. */}
        {currentRoom &&
          byId.get(currentRoom) &&
          (() => {
            const p = center(byId.get(currentRoom)!);
            const size = 72;
            return (
              <image
                className="token"
                href={dataUri(renderPlayerToken(session.state.player.classId, size))}
                x={p.x - size / 2}
                y={p.y - size * 0.62}
                width={size}
                height={size}
              />
            );
          })()}
      </svg>

      <div className="map__legend">
        {(['npc', 'combat', 'treasure', 'trap', 'puzzle', 'secret', 'boss'] as const).map((k) => (
          <span key={k} className="legend__item">
            <i style={{ background: KIND_STYLE[k].color }} />
            {KIND_STYLE[k].label}
          </span>
        ))}
      </div>
    </div>
  );
}
